/**
 * SA-1127: the Inbox toolbar, two rows with fixed roles for every
 * deployment shape (R3). Row 1: the view tabs, the search, then at its end
 * Archive older than…, Filters, the sort and Group by sender. Row 2, which
 * wraps: the channel chips, the console's status, Needs review and Faults
 * chips, and the triage filters that are on. Which control shows is
 * SA-1092's shape rule (`inboxShape.ts`), unchanged; only where each sits
 * moved. Presentational: the page holds every value and setter.
 */
import { Archive, Search, Users } from 'lucide-react'
import { Badge, Button, Input, Tip } from '@/components/ui'
import type { Mailbox } from '@/services/mailboxes'
import type { InboxViewTab } from '@/types/inbox'
import { cn } from '@/utils'
import { channelIcon } from './channels'
import { CHIP_ON_CLASS, FilterChip } from './FilterChip'
import { InboxViewTabs } from './InboxViewTabs'
import type { InboxShape } from './inboxShape'
import { sortOptions } from './sortText'
import { ActiveTriageFilters, TriageFiltersMenu } from './TriageFiltersMenu'
import { tourAnchor } from '@/components/tour/tourAnchor'

/** The channel chips' words (SA-1092 §2.2); the icons are `channelIcon`'s. */
const CHANNEL_CHIP_LABELS: Record<string, string> = {
  all: 'All', whatsapp: 'WhatsApp', web: 'Web', email: 'Email', 'whatsapp-group': 'WhatsApp group',
}
const STATUSES = ['active', 'waiting', 'human'] as const

export interface InboxToolbarProps {
  shape: InboxShape
  viewTab: InboxViewTab
  onViewTab: (tab: InboxViewTab) => void
  /** The Triage tab's count; undefined = no Triage tab. */
  triageCount: number | undefined
  searchQuery: string
  onSearch: (query: string) => void
  channelFilter: string | null
  onChannel: (channel: string | null) => void
  statusFilter: string | null
  onStatus: (status: string | null) => void
  reviewFilter: boolean
  onReview: (on: boolean) => void
  faultsView: boolean
  onFaults: (on: boolean) => void
  faultCount: number
  sortBy: string
  onSort: (sort: string) => void
  groupBySender: boolean
  onGroupBySender: (on: boolean) => void
  /** `inbox:archive`; the bulk archive shows on the Resolved tab with it. */
  canArchive: boolean
  onBulkArchive: () => void
  /** The mailbox list `useInboxFilterChips` read, for the triage filters. */
  mailboxes: Mailbox[] | undefined
}

function Rule() {
  return <span aria-hidden="true" className="mx-1 h-5 w-px bg-border" />
}

export function InboxToolbar(p: InboxToolbarProps) {
  const { shape } = p
  const showStatus = p.viewTab === 'open' && shape.statusRow
  const showRow2 = shape.channels.length > 0 || showStatus || shape.needsReview || shape.faults || shape.triageChips
  return (
    <div data-testid="inbox-toolbar" className="border-b border-border px-3 py-2.5">
      <div data-testid="inbox-toolbar-row-1" className="flex flex-wrap items-center gap-2.5">
        <InboxViewTabs value={p.viewTab} onChange={p.onViewTab} triageCount={p.triageCount} />
        <div className="relative min-w-40 max-w-[260px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="text"
            placeholder={shape.searchPlaceholder}
            {...tourAnchor('inbox.toolbar.search')}
            value={p.searchQuery}
            onChange={(e) => p.onSearch(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          {p.viewTab === 'resolved' && p.canArchive && (
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={p.onBulkArchive}>
              <Archive className="size-3.5" aria-hidden="true" />
              Archive older than…
            </Button>
          )}
          {shape.triageChips && <TriageFiltersMenu mailboxes={p.mailboxes} />}
          {shape.sortSelect && (
            <Tip text="Sort conversations">
              <select
                value={p.sortBy}
                onChange={(e) => p.onSort(e.target.value)}
                aria-label="Sort conversations"
                className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground shadow-xs hover:bg-accent focus-visible:outline-hidden focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                {sortOptions(p.channelFilter, shape.kind).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Tip>
          )}
          {shape.groupBySender && (
            <Tip text="Group by sender">
              <Button
                variant="outline"
                size="sm"
                aria-pressed={p.groupBySender}
                aria-label="Group by sender"
                onClick={() => p.onGroupBySender(!p.groupBySender)}
                className={cn('size-8 p-0', p.groupBySender && CHIP_ON_CLASS)}
              >
                <Users className="size-3.5" aria-hidden="true" />
              </Button>
            </Tip>
          )}
        </div>
      </div>

      {showRow2 && (
        <div data-testid="inbox-toolbar-row-2" className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-2">
          {shape.channels.length > 0 && (
            <div role="group" aria-label="Channel" {...tourAnchor('inbox.toolbar.channels')} className="flex flex-wrap items-center gap-1.5">
              {shape.channels.map((channel) => {
                const value = channel === 'all' ? null : channel
                return (
                  <FilterChip
                    key={channel}
                    on={p.channelFilter === value}
                    onClick={() => p.onChannel(value)}
                    icon={channel === 'all' ? undefined : channelIcon(channel)}
                  >
                    {CHANNEL_CHIP_LABELS[channel] ?? channel}
                  </FilterChip>
                )
              })}
            </div>
          )}
          {showStatus && (
            <>
              <Rule />
              <div role="group" aria-label="Status" className="flex flex-wrap items-center gap-1.5">
                {STATUSES.map((status) => (
                  <FilterChip
                    key={status}
                    on={p.statusFilter === status}
                    onClick={() => p.onStatus(p.statusFilter === status ? null : status)}
                    className="capitalize"
                  >
                    {status}
                  </FilterChip>
                ))}
              </div>
            </>
          )}
          {(shape.needsReview || shape.faults) && (
            <>
              <Rule />
              <div role="group" aria-label="Review and faults" className="flex flex-wrap items-center gap-1.5">
                {shape.needsReview && (
                  <FilterChip on={p.reviewFilter} onClick={() => p.onReview(!p.reviewFilter)}>Needs review</FilterChip>
                )}
                {shape.faults && (
                  <FilterChip on={p.faultsView} onClick={() => p.onFaults(!p.faultsView)}>
                    Faults
                    {p.faultCount > 0 && (
                      <Badge variant="destructive-soft" className="px-1.5 py-0 text-[10px]">{p.faultCount}</Badge>
                    )}
                  </FilterChip>
                )}
              </div>
            </>
          )}
          {shape.triageChips && <ActiveTriageFilters mailboxes={p.mailboxes} />}
        </div>
      )}
    </div>
  )
}
